import { CardAnalytics } from '../../../components/library/card-analytics/CardAnalytics';
import { useAppDispatch, useAppSelector } from '../../../contexts/hooks';
import { EReportId } from '../../../types/types.dashboard';
import { useApiDashBoard } from '../../../api/api.dashboards';
import { FilteredOrders } from '../../../types/types.orders';
import { setFilteresToOrders } from '../../../contexts/slices/orders';
import { useState } from 'react';
import { ConvertLocalDate } from '../../../utils/functions';
import { useScreenSize } from '../../../utils/media-query';
import ProgressBar from 'devextreme-react/progress-bar';
import { useNavigate } from 'react-router-dom';

export const BoardOrdersByServices = () => {
  const { getData } = useApiDashBoard();
  const dispatch = useAppDispatch();
  const [dataLoading, setDataLoading] = useState(false);
  const { isXSmall, isSmall } = useScreenSize();
  const dashboard = useAppSelector((x) => x.dashboards.dashboards.find((x) => x.Db_Id === EReportId.OrdersByServices));
  const navigate = useNavigate();

  const handleViewClick = (data: any) => {
    const today = ConvertLocalDate(new Date());
    const filters: FilteredOrders = {
      Servicios: [data.Se_Id],
      Fechas: [today, today],
      Areas: [],
      Estudios: [],
      Estados: [],
      Redirect: true,
    };
    setDataLoading(true);
    dispatch(setFilteresToOrders(filters));
    navigate('/admin-orders'); // Sin state, preserva el tab actual del usuario
  };

  if (!dashboard) {
    return <CardAnalytics title='Órdenes por servicio' contentClass='sales' />;
  }
  const handleRefreshClick = () => {
    getData(EReportId.OrdersByServices);
  };
  // Obtener el máximo de órdenes para calcular porcentajes
  const maxOrders = dashboard.Data ? Math.max(...dashboard.Data.map((item: any) => item.Total_Ordenes || 0)) : 0;

  // Calcular el total de todas las órdenes
  const totalOrders = dashboard.Data?.reduce((sum: number, item: any) => sum + (item.Total_Ordenes || 0), 0) || 0;

  // Variable para pantallas pequeñas
  const isSmallScreen = isXSmall || isSmall;

  // Render de cada servicio como una card compacta
  const renderServiceCard = (service: any, index: number) => {
    const serviceColor = service.Se_Color || '#6c757d';
    const total = service.Total_Ordenes || 0;
    const percentage = maxOrders > 0 ? (total / maxOrders) * 100 : 0;
    const sharePercentage = totalOrders > 0 ? ((total / totalOrders) * 100).toFixed(1) : '0';

    return (
      <div
        key={service.Se_Id}
        style={{
          background: 'white',
          border: '1px solid #e9ecef',
          borderRadius: '8px',
          padding: '10px 11px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
          transition: 'all 0.2s ease',
          cursor: 'pointer',
          position: 'relative',
          overflow: 'hidden',
          minHeight: '88px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
        }}
        onClick={() => handleViewClick(service)}>
        {/* Línea de color superior */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '3px',
            background: serviceColor,
          }}
        />

        {/* Header con indicador de color y total */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '6px',
          }}>
          <div
            style={{
              width: '13px',
              height: '13px',
              borderRadius: '50%',
              backgroundColor: serviceColor,
              flexShrink: 0,
              boxShadow: `0 0 0 2px ${serviceColor}33`,
            }}
          />

          <div
            style={{
              background: `${serviceColor}15`,
              color: serviceColor,
              padding: '2px 8px',
              borderRadius: '12px',
              fontSize: '0.75rem',
              fontWeight: '700',
              minWidth: '35px',
              textAlign: 'center',
            }}>
            {total.toLocaleString()}
          </div>
        </div>

        {/* Nombre del servicio */}
        <div
          style={{
            fontSize: '0.8rem',
            fontWeight: '600',
            color: '#2c3e50',
            marginBottom: '6px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            lineHeight: '1.2',
          }}
          title={service.Se_Nombre || 'Sin nombre'}>
          {service.Se_Nombre || 'Sin nombre'}
        </div>

        {/* Barra de progreso compacta */}
        <div style={{ marginBottom: '4px' }}>
          <ProgressBar
            value={percentage}
            max={100}
            showStatus={false}
            height={5}
            style={{
              background: '#f1f3f4',
              borderRadius: '3px',
              overflow: 'hidden',
            }}
          />
        </div>

        {/* Porcentaje del total */}
        <div
          style={{
            fontSize: '0.7rem',
            color: '#6c757d',
            textAlign: 'center',
            fontWeight: '500',
          }}>
          {sharePercentage}% del total
        </div>
      </div>
    );
  };

  return (
    <CardAnalytics title={`Órdenes por servicios`} contentClass='sales' lastUpdate={dashboard.UltimaActualizacion} isLoading={dashboard.Loading || dataLoading} onRefreshClick={handleRefreshClick}>
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header con estadísticas generales */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '6px 4px 10px',
            borderBottom: '1px solid #e9ecef',
            marginBottom: '10px',
          }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div
              style={{
                background: '#f8f9fa',
                padding: '4px 8px',
                borderRadius: '6px',
                fontSize: '0.75rem',
                fontWeight: '600',
                color: '#495057',
              }}>
              {dashboard.Data?.length || 0} Servicios
            </div>
            <div
              style={{
                background: '#e7f3ff',
                color: '#0066cc',
                padding: '4px 8px',
                borderRadius: '6px',
                fontSize: '0.75rem',
                fontWeight: '600',
              }}>
              {totalOrders.toLocaleString()} Órdenes
            </div>
          </div>
        </div>

        {/* Grid de servicios */}
        <div
          style={{
            flex: 1,
            overflow: 'auto',
            padding: '0 4px',
          }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: isSmallScreen ? 'repeat(auto-fit, minmax(140px, 1fr))' : 'repeat(auto-fit, minmax(160px, 1fr))',
              gap: '10px',
              gridAutoRows: 'min-content',
            }}>
            {dashboard.Data?.map((service: any, index: number) => renderServiceCard(service, index))}
          </div>
        </div>
      </div>
    </CardAnalytics>
  );
};
